// CoachCard — pixel-match of cards.jsx (CoachCard) from the Design Bundle.
// Same chrome as PlayerCard but with role pill instead of jersey number,
// a "license" pill instead of foot, and an experience/formation stat row.
//
// Design fields mapped from the existing /users/public/profile/coach/
// API response:
//
//   data.name          ←  TSCoach.name
//   data.photo_url     ←  TSCoach.logo / SMCoach.image_path fallback
//   data.role          ←  derived from TSCoach.type (Head Coach / Interim)
//   data.club          ←  TSCoach.team { name, logo_url }
//   data.nationality   ←  TSCoach.nationality (object or 3-letter string)
//   data.age           ←  TSCoach.age
//   data.license       ←  Platform Coach.license_number IF linked, else null
//   data.formation     ←  TSCoach.preferred_formation
//
// Per spec: when license/formation are missing we just leave them out —
// no placeholder text. Card stays visually balanced because the design
// already accommodates optional rows.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { TpMark } from "./TpMark";

function VerifiedBadge({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-label="Verified" style={{ flexShrink: 0 }}>
      <path
        d="M10 1 L12.2 2.6 L14.9 2.3 L16 4.8 L18.3 6.3 L17.7 9 L18.3 11.7 L16 13.2 L14.9 15.7 L12.2 15.4 L10 17 L7.8 15.4 L5.1 15.7 L4 13.2 L1.7 11.7 L2.3 9 L1.7 6.3 L4 4.8 L5.1 2.3 L7.8 2.6 Z"
        fill="#4A90D9"
      />
      <path d="M6 10.2 L8.8 13 L14 7.4" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowRight({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 7 H11 M7.5 3 L11 7 L7.5 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Avatar({ photoUrl, fallbackInitials }) {
  const [broken, setBroken] = useState(false);
  if (photoUrl && !broken) {
    return (
      <div className="tp-avatar">
        <img src={photoUrl} alt="" onError={() => setBroken(true)} />
      </div>
    );
  }
  return (
    <div className="tp-avatar">
      <div className="tp-avatar-initials">{fallbackInitials || "?"}</div>
    </div>
  );
}

function ClubBadge({ club }) {
  const [broken, setBroken] = useState(false);
  if (!club) return null;
  // (hook above the early return — React rules-of-hooks)
  const initials =
    (club.name || "")
      .replace(/[^A-Za-z]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "FC";
  if (club.logo_url && !broken) {
    return <img className="tp-club-logo" src={club.logo_url} alt="" onError={() => setBroken(true)} />;
  }
  return <span className="tp-club-logo-fallback">{initials}</span>;
}

export default function CoachCard({ data }) {
  const navigate = useNavigate();
  if (!data) return null;

  const goToProfile = () => navigate(data.profile_url);
  const fallbackInitials = (data.name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  // "Pinned" badge text on the avatar. Default to "COACH" for all coach types
  // since the role label below already differentiates Head vs Interim.
  const badgeText = "COACH";

  return (
    <div
      className="tp-card"
      onClick={goToProfile}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToProfile()}
      title={`Open ${data.name}'s profile`}
    >
      <div className="tp-card-inner">
        <div className="tp-row tp-row-head">
          <div className="tp-avatar-wrap">
            <Avatar photoUrl={data.photo_url} fallbackInitials={fallbackInitials} />
            <div className="tp-role-pill">{badgeText}</div>
          </div>

          <div className="tp-head-text">
            <div className="tp-name-row">
              <span className="tp-name">{data.name}</span>
              {data.verified && <VerifiedBadge size={16} />}
            </div>
            <div className="tp-sub">
              {data.role && <span className="tp-pos-primary">{data.role}</span>}
            </div>
          </div>

          <div className="tp-corner-mark" title="TransferPitch">
            <TpMark size={26} />
          </div>
        </div>

        {/* CLUB row */}
        {data.club && data.club.name && (
          <div className="tp-row tp-row-club" onClick={(e) => e.stopPropagation()}>
            <ClubBadge club={data.club} />
            <span className="tp-club-name">{data.club.name}</span>
          </div>
        )}

        {/* STATS row */}
        {(data.nationality || data.age != null || data.license) && (
          <div className="tp-row tp-row-stats">
            {data.nationality?.flag_url && <img className="tp-flag" src={data.nationality.flag_url} alt="" />}
            {data.nationality?.name && <span className="tp-stat">{data.nationality.name}</span>}
            {data.age != null && (
              <>
                {data.nationality && <span className="tp-dot">·</span>}
                <span className="tp-stat">{data.age}y</span>
              </>
            )}
            {data.license && (
              <>
                <span className="tp-dot">·</span>
                <span className="tp-stat tp-license">{data.license}</span>
              </>
            )}
          </div>
        )}

        {/* FORMATION row — analogue to player's "Market value" */}
        {data.formation && (
          <div className="tp-row tp-row-mv">
            <span className="tp-mv-label">Formation</span>
            <span className="tp-mv-amount">{data.formation}</span>
          </div>
        )}

        <div className="tp-row tp-row-cta">
          <span className="tp-cta-text">View full profile</span>
          <ArrowRight />
        </div>
      </div>
    </div>
  );
}
